import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import { theme as baseTheme } from './tokens';
import type { Theme, ThemeOverride, ThemeContextValue } from './types';
import {
  mergeTheme,
  deepMerge,
  generateCssVariables,
  applyCssVariables,
  exportThemeToJson,
  parseThemeJson,
  parseTenantThemeOverride,
} from './themeUtils';

const ThemeContext = createContext<ThemeContextValue | null>(null);

export const STORAGE_KEY = 'motr_theme_override';
const TENANT_THEME_CACHE_KEY = 'motr_tenant_theme_cache';

interface ThemeProviderProps {
  children: ReactNode;
  /** Tenant theme override JSON from settings */
  tenantThemeOverride?: string;
  /** Callback when theme is saved (to persist to backend) */
  onSaveTheme?: (themeJson: string) => Promise<void>;
  /** Whether theme is being loaded from backend */
  isLoading?: boolean;
}

/**
 * Theme Provider - Provides customizable theme with tenant support
 *
 * Theme layers (in order of precedence):
 * 1. Base theme (from tokens.ts)
 * 2. Tenant theme override (from motr.Settings.ThemeOverride)
 * 3. User customizations (for admin preview)
 */
export const ThemeProvider = ({
  children,
  tenantThemeOverride,
  onSaveTheme,
  isLoading = false,
}: ThemeProviderProps) => {
  // Parse tenant override, falling back to cached version for instant load
  const tenantOverride = useMemo(() => {
    if (tenantThemeOverride) {
      // Cache for next page load to avoid theme flash
      try {
        localStorage.setItem(TENANT_THEME_CACHE_KEY, tenantThemeOverride);
      } catch { /* quota exceeded — ignore */ }
      return parseTenantThemeOverride(tenantThemeOverride);
    }
    // No API response yet — use cached tenant theme from last session
    if (isLoading) {
      try {
        const cached = localStorage.getItem(TENANT_THEME_CACHE_KEY);
        if (cached) return parseTenantThemeOverride(cached);
      } catch { /* ignore */ }
    }
    return {};
  }, [tenantThemeOverride, isLoading]);

  // Local override state (for admin preview)
  const [localOverride, setLocalOverride] = useState<ThemeOverride>(() => {
    // Try to load from storage (for admin editing session)
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        return JSON.parse(stored) as ThemeOverride;
      } catch {
        return {};
      }
    }
    return {};
  });

  // Merge all theme layers
  const mergedTheme = useMemo((): Theme => {
    // Start with base theme
    let result = baseTheme;

    // Apply tenant override
    if (Object.keys(tenantOverride).length > 0) {
      result = mergeTheme(tenantOverride);
    }

    // Apply local override (for admin preview) — deep merge so nested
    // properties (e.g. sidebar.text) don't clobber sibling keys (e.g. sidebar.background)
    if (Object.keys(localOverride).length > 0) {
      result = mergeTheme(deepMerge(tenantOverride as Record<string, unknown>, localOverride as Record<string, unknown>) as ThemeOverride);
    }

    return result;
  }, [tenantOverride, localOverride]);

  // Generate CSS variables
  const cssVariables = useMemo(
    () => generateCssVariables(mergedTheme),
    [mergedTheme]
  );

  // Apply CSS variables to document
  useEffect(() => {
    applyCssVariables(cssVariables);
  }, [cssVariables]);

  // Set theme override
  const setOverride = useCallback((override: ThemeOverride) => {
    setLocalOverride(override);
    // Store in session for persistence during editing
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(override));
  }, []);

  // Save theme to backend (tenant-level)
  const saveThemeToBackend = useCallback(async (): Promise<boolean> => {
    if (!onSaveTheme) return false;

    try {
      const currentOverride = deepMerge(tenantOverride as Record<string, unknown>, localOverride as Record<string, unknown>) as ThemeOverride;
      const json = exportThemeToJson(currentOverride, 'mot-r Theme');
      await onSaveTheme(json);
      return true;
    } catch (error) {
      console.error('[Theme] Failed to save theme to backend:', error);
      return false;
    }
  }, [onSaveTheme, tenantOverride, localOverride]);

  // Reset to base theme
  const resetTheme = useCallback(() => {
    setLocalOverride({});
    sessionStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(TENANT_THEME_CACHE_KEY);
  }, []);

  // Export theme as JSON
  const exportTheme = useCallback((): string => {
    const currentOverride = deepMerge(tenantOverride as Record<string, unknown>, localOverride as Record<string, unknown>) as ThemeOverride;
    return exportThemeToJson(currentOverride, 'mot-r Theme');
  }, [tenantOverride, localOverride]);

  // Import theme from JSON
  const importTheme = useCallback((json: string): boolean => {
    const parsed = parseThemeJson(json);
    if (parsed) {
      setOverride(parsed);
      return true;
    }
    return false;
  }, [setOverride]);

  // Check if any override is applied
  const hasOverride =
    Object.keys(tenantOverride).length > 0 ||
    Object.keys(localOverride).length > 0;

  const value: ThemeContextValue = useMemo(
    () => ({
      theme: mergedTheme,
      hasOverride,
      isLoading,
      setOverride,
      resetTheme,
      exportTheme,
      importTheme,
      saveThemeToBackend,
      cssVariables,
    }),
    [
      mergedTheme,
      hasOverride,
      isLoading,
      setOverride,
      resetTheme,
      exportTheme,
      importTheme,
      saveThemeToBackend,
      cssVariables,
    ]
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

/**
 * Hook to access theme context
 */
export const useThemeContext = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeContext must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeProvider;
